import type { ReactNode } from 'react';
import { AlertCircle, ArrowRight, CheckCircle2, Circle, Clock, Hourglass, Layout, Lock } from 'lucide-react';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';

/**
 * MilestoneDependencyGraph — the recommendation engine's milestones by domain, in dependency
 * order, each marked not started, in progress (or stale), done or locked, with what it needs and
 * what it unlocks in its tooltip. For the firm (`showRisks`), a milestone with a detected risk
 * carries a red or amber dot and its risks in the tooltip. Moved from EngagementPlan
 * (RESTORE_PLAN R2.6); the engine's `graph` comes from `GET /client/recommendations/:companyId`.
 */

export interface MilestoneNode {
  id: string;
  name: string;
  domain: string;
  status?: string;
  score?: number;
  isStale?: boolean;
  explanation?: string;
  dependencies?: string[];
  dependents?: string[];
  risks?: Array<{ level?: string; description?: string }>;
}

/** The domains in the engine's order; a node in any other domain is drawn after them. */
export const MILESTONE_DOMAINS = ['Global', 'SRM', 'Incident', 'Change', 'Problem', 'PIR', 'Dashboards', 'Queues'];

export function nodesByDomain(graph: MilestoneNode[]): Array<{ domain: string; nodes: MilestoneNode[] }> {
  const groups = new Map<string, MilestoneNode[]>();
  for (const node of graph ?? []) {
    const domain = node?.domain || 'Global';
    if (!groups.has(domain)) groups.set(domain, []);
    groups.get(domain)!.push(node);
  }
  const known = MILESTONE_DOMAINS.filter((d) => groups.has(d));
  const others = [...groups.keys()].filter((d) => !MILESTONE_DOMAINS.includes(d));
  return [...known, ...others].map((domain) => ({ domain, nodes: groups.get(domain)! }));
}

export type MilestoneState = 'done' | 'active' | 'stale' | 'locked' | 'not_started';

export function milestoneState(node: MilestoneNode): MilestoneState {
  if (node.status === 'completed' || node.status === 'locked') return 'done';
  if (node.status === 'in_progress') return node.isStale ? 'stale' : 'active';
  if (node.score === -1000 || node.status === 'blocked') return 'locked';
  return 'not_started';
}

const STATE_STYLE: Record<MilestoneState, { tone: string; icon: ReactNode; label: string }> = {
  done: { tone: 'bg-green-50 border-green-200 text-green-700', icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Done' },
  active: { tone: 'bg-blue-50 border-blue-200 text-blue-700 ring-2 ring-blue-100', icon: <Clock className="size-4" aria-hidden="true" />, label: 'In progress' },
  stale: { tone: 'bg-amber-50 border-amber-200 text-amber-700', icon: <Hourglass className="size-4" aria-hidden="true" />, label: 'Stalled' },
  locked: { tone: 'bg-red-50 border-red-200 text-red-400 opacity-70', icon: <Lock className="size-4" aria-hidden="true" />, label: 'Waiting on a dependency' },
  not_started: { tone: 'bg-gray-100 border-gray-200 text-gray-400', icon: <Circle className="size-4" aria-hidden="true" />, label: 'Not started' },
};

interface MilestoneDependencyGraphProps {
  graph: MilestoneNode[];
  /** The firm sees risk dots and the detected risks in each tooltip. */
  showRisks?: boolean;
}

export function MilestoneDependencyGraph({ graph, showRisks = false }: MilestoneDependencyGraphProps) {
  const domains = nodesByDomain(graph);
  return (
    <section id="milestone-plan" className="space-y-4 scroll-mt-20" aria-labelledby="milestone-plan-heading" data-testid="milestone-graph">
      <h3 id="milestone-plan-heading" className="flex items-center gap-2">
        <Layout className="size-5 text-gray-500" aria-hidden="true" />
        Milestone dependency graph
      </h3>
      {domains.length === 0 ? (
        <p className="text-muted-foreground">No milestones yet: the plan fills in once discovery starts.</p>
      ) : (
        <div className="grid gap-4">
          {domains.map(({ domain, nodes }) => (
            <Card key={domain} className="overflow-hidden border-l-4 border-l-gray-300" data-testid="milestone-domain">
              <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2">
                <h4>{domain} domain</h4>
                <span className="text-muted-foreground">{nodes.length} {nodes.length === 1 ? 'milestone' : 'milestones'}</span>
              </div>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  {nodes.map((node, index) => {
                    const state = milestoneState(node);
                    const style = STATE_STYLE[state];
                    const risks = node.risks ?? [];
                    const high = risks.some((r) => r.level === 'high');
                    const medium = risks.some((r) => r.level === 'medium');
                    const riskTone = showRisks && state !== 'done' ? (high ? ' ring-1 ring-red-400 border-red-300' : medium ? ' ring-1 ring-amber-400 border-amber-300' : '') : '';
                    return (
                      <div key={node.id} className="relative flex items-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                data-testid="milestone"
                                data-state={state}
                                aria-label={`${node.name}: ${style.label}${showRisks && risks.length > 0 && state !== 'done' ? `, ${risks.length} ${risks.length === 1 ? 'risk' : 'risks'}` : ''}`}
                                className={`relative flex min-w-[140px] cursor-default items-center justify-between gap-2 rounded-lg border px-3 py-2 transition-all hover:shadow-md ${style.tone}${riskTone}`}
                              >
                                {showRisks && (high || medium) && state !== 'done' && (
                                  <span className={`absolute -right-1 -top-1 size-3 rounded-full border border-white shadow-sm ${high ? 'bg-red-500' : 'bg-amber-500'}`} data-testid="milestone-risk-dot" />
                                )}
                                <span className="max-w-[120px] truncate">{node.name}</span>
                                {style.icon}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="z-50 max-w-xs">
                              <p className="flex items-center gap-2">
                                {node.name}
                                {node.isStale && <Badge variant="secondary" className="bg-amber-100 text-amber-800">Stale</Badge>}
                              </p>
                              <p className="mb-2 opacity-80">{node.explanation || 'No details available.'}</p>
                              {showRisks && risks.length > 0 && (
                                <div className="mb-2 rounded border border-red-100 bg-red-50 p-2 text-red-800">
                                  <span className="flex items-center gap-1"><AlertCircle className="size-3" aria-hidden="true" /> Detected risks</span>
                                  <ul className="mt-1 space-y-1">
                                    {risks.map((r, i) => <li key={i}>• {r.description}</li>)}
                                  </ul>
                                </div>
                              )}
                              {(node.dependencies?.length ?? 0) > 0 && (
                                <div className="mb-1">
                                  <span className="opacity-70">Needs:</span>
                                  <div className="mt-0.5 flex flex-wrap gap-1">
                                    {node.dependencies!.map((d) => <Badge key={d} variant="outline">{formatCategoryLabel(d)}</Badge>)}
                                  </div>
                                </div>
                              )}
                              {(node.dependents?.length ?? 0) > 0 && (
                                <div>
                                  <span className="opacity-70">Unlocks:</span>
                                  <div className="mt-0.5 flex flex-wrap gap-1">
                                    {node.dependents!.map((d) => <Badge key={d} variant="outline" className="border-green-200 bg-green-50 text-green-700">{formatCategoryLabel(d)}</Badge>)}
                                  </div>
                                </div>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {index < nodes.length - 1 && <ArrowRight className="mx-2 hidden size-4 text-gray-300 md:block" aria-hidden="true" />}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
